import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import type { DocumentRef } from '../../lib/documentReader'
import { formatNumberString, formatOfficerDate, formatOfficerValue } from '../../lib/officerFormat'
import type { BookFieldDefinition } from '../rkdecks/bookReview'
import { useRead } from '../navigation/useRead'
import DocumentLink from '../documents/DocumentLink'
import SourceDocumentList from '../documents/SourceDocumentList'
import { ErrorState, Skeleton } from '../state'
import { recordHref, type ViewScope } from '../navigation/context'
import './decisions.css'
import './subchain-close.css'

type SupplyInterval = {
  reference_id: string
  status: 'balanced' | 'break' | 'unknown'
  expected: string | null
  stated: string
  delta: string | null
  reference: { financial_date: string | null }
  previous_reference: { financial_date: string | null } | null
  waived_provisional?: { trade_id: string }[]
}
type CloseObject = {
  kind: string
  id: string
  fields: Record<string, unknown>
  dated_supply_intervals?: SupplyInterval[]
}
type Movement = {
  trade_id: string
  date: string | null
  delta: string | null
  document_id: string | null
  transaction_type?: string | null
  currency?: string | null
  provisional?: boolean
}
type BalanceComparison = {
  balance_id?: string
  date: string
  expected_from_actual_trades: string
  stated: string
  residual: string
  trade_ids?: string[]
  waived_provisional?: Movement[]
}
type Balance = {
  position_id: string
  value: string | null
  value_date: string | null
  currency: string | null
  base: { date: string; value: string; kind?: string; document_id?: string | null } | null
  movements: Movement[]
  provisional?: Movement[]
  comparisons: BalanceComparison[]
}
type CloseScope = { fund_id?: string | null; client_id?: string | null }
interface CloseReview {
  scope?: CloseScope | null
  id: string
  as_of_date: string
  status: string
  check_status: string
  check_run_id: string | null
  input_fingerprint: string | null
  can_close: boolean
  snapshot_current?: boolean
  historical_only?: boolean
  objects: CloseObject[]
  balances: Balance[]
  documents: DocumentRef[]
  checks: {
    requirement_code: string
    label?: string
    target_type: string
    target_id: string
    result: string
    detail: string | null
  }[]
  blockers: string[]
  prior_periods: { id: string; as_of_date: string; status: string }[]
  joint_review: {
    status: string
    error?: string | null
    result?: {
      summary: string
      findings: {
        code: string
        detail: string
        target_type: 'entity' | 'instrument' | 'position' | 'trade'
        target_id: string
        document_id: string | null
        page: number | null
        quote?: string | null
        quote_method?: 'native_text' | 'visual'
      }[]
    } | null
  } | null
}
const mainFields = new Set([
  'canonical_name',
  'name',
  'legal_form',
  'registered_address',
  'jurisdiction',
  'registry_id',
  'outstanding_units',
  'nominal_amount',
  'currency',
  'units_held',
  'units_currency',
  'balance_as_of',
  'transaction_type',
  'units_delta',
  'effective_date',
  'amount',
])
function objectTitle(object: CloseObject) {
  return String(
    object.fields.canonical_name ??
      object.fields.name ??
      (object.kind === 'trade'
        ? 'Movement'
        : object.kind === 'position'
          ? 'Position'
          : object.kind === 'instrument'
            ? 'Instrument'
            : 'Entity')
  )
}

function criteriaLabel(check: CloseReview['checks'][number]): string {
  if (check.label) return check.label
  return check.requirement_code
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

// A reconciliation difference must not round to zero or hide unequal quantities.
function formatIntervalQuantity(value: string): string {
  const parts = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value)
  if (!parts) return value
  const fraction = (parts[3] ?? '').replace(/0+$/, '')
  return `${parts[1]}${parts[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`
}

const CATALOGUE_KINDS = ['entity', 'instrument', 'position', 'trade'] as const

function hasRecordCatalogue(
  value: Record<string, { fields: BookFieldDefinition[] }> | undefined
): boolean {
  return Boolean(value && CATALOGUE_KINDS.every((kind) => Array.isArray(value[kind]?.fields)))
}

function isoDate(value: unknown): string | null {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null
}

function objectDate(object: CloseObject): string | null {
  return isoDate(object.fields.effective_date)
}

type TradeView = { object: CloseObject; unknownDate: boolean }

/** Review objects include issuer/cash trades that have no balance movement row. */
function actualTradeObjects(review: CloseReview): TradeView[] {
  const provisionalIds = new Set(
    review.balances.flatMap((balance) => [
      ...(balance.provisional ?? []).map((movement) => movement.trade_id),
      ...balance.movements
        .filter((movement) => movement.provisional)
        .map((movement) => movement.trade_id),
    ])
  )
  const cutoffByPosition = new Map(
    review.balances.flatMap((balance) => {
      const date = isoDate(balance.base?.date)
      return date ? [[balance.position_id, date] as const] : []
    })
  )
  const cutoffs = [...cutoffByPosition.values()].sort()
  const latestCutoff = cutoffs[cutoffs.length - 1] ?? null
  const asOf = isoDate(review.as_of_date)
  return review.objects.flatMap((object): TradeView[] => {
    if (object.kind !== 'trade' || provisionalIds.has(object.id)) return []
    const date = objectDate(object)
    if (!date) return [{ object, unknownDate: true }]
    if (asOf && date > asOf) return []
    const positionId =
      typeof object.fields.position_id === 'string' ? object.fields.position_id : null
    const cutoff = (positionId && cutoffByPosition.get(positionId)) ?? latestCutoff
    if (cutoff && date <= cutoff) return []
    return [{ object, unknownDate: false }]
  })
}

function closeViewScope(
  scope: CloseScope | Record<string, unknown> | null | undefined
): ViewScope | null {
  const fundId = typeof scope?.fund_id === 'string' ? scope.fund_id : null
  const clientId = typeof scope?.client_id === 'string' ? scope.client_id : null
  if (fundId) return { kind: 'fund', fund_id: fundId, client_id: clientId }
  if (clientId) return { kind: 'client', client_id: clientId }
  return null
}

function findingRecordHref(
  review: CloseReview,
  fallbackScope: Record<string, unknown> | undefined,
  finding: Pick<
    NonNullable<NonNullable<CloseReview['joint_review']>['result']>['findings'][number],
    'target_type' | 'target_id'
  >
): string | null {
  const scope = closeViewScope(review.scope) ?? closeViewScope(fallbackScope)
  if (!scope || !finding.target_id) return null
  try {
    return recordHref(scope, { focus: finding.target_type + ':' + finding.target_id })
  } catch {
    return null
  }
}

function findingDoorLabel(
  review: CloseReview,
  finding: NonNullable<NonNullable<CloseReview['joint_review']>['result']>['findings'][number]
): string {
  if (finding.target_type === 'trade') {
    const target = review.objects.find(
      (object) => object.kind === finding.target_type && object.id === finding.target_id
    )
    const month = objectDate(target ?? { kind: 'trade', id: '', fields: {} })
    const words = month ? formatOfficerDate(month)?.split(' ') : null
    if (words?.[1]) return 'Review recorded ' + words[1] + ' trade'
  }
  return 'Review recorded ' + finding.target_type + ' in Record'
}

function CurrentObject({
  object,
  catalogue,
  balance,
  unknownDate = false,
}: {
  object: CloseObject
  catalogue: BookFieldDefinition[]
  balance?: Balance
  unknownDate?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const values: Record<string, unknown> = {
    ...object.fields,
    ...(balance ? { units_held: balance.value, balance_as_of: balance.value_date } : {}),
  }
  const fields = catalogue.filter(
    (field) => values[field.key] !== undefined && values[field.key] !== null
  )
  const visible = expanded ? fields : fields.filter((field) => mainFields.has(field.key))
  return (
    <section className="subchain-close__object">
      <h3>{objectTitle(object)}</h3>
      {unknownDate ? (
        <p className="subchain-close__attention" role="alert">
          This recorded movement has no effective date. Review its date before closing.
        </p>
      ) : null}
      <dl>
        {visible.map((field) => (
          <div key={field.key}>
            <dt>{field.label}</dt>
            <dd>{formatOfficerValue(values[field.key])}</dd>
          </div>
        ))}
      </dl>
      {fields.length > visible.length || expanded ? (
        <button
          type="button"
          className="cn-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide other fields' : 'See full object'}
        </button>
      ) : null}
    </section>
  )
}

export default function SubchainClosePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const data = (task.task_data ?? {}) as {
    closure_id?: string
    scope?: Record<string, unknown>
    as_of_date?: string
  }
  const [selected, setSelected] = useState(data.closure_id ?? null)
  const [refresh, setRefresh] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const read = useRead<CloseReview>(
    selected ? `/api/subchain-closures/${encodeURIComponent(selected)}` : null,
    refresh
  )
  const catalogue = useRead<Record<string, { fields: BookFieldDefinition[] }>>('/api/record-fields')
  const review = read.value
  useEffect(() => {
    if (review?.check_status !== 'running') return
    const timer = setTimeout(() => setRefresh((value) => value + 1), 1500)
    return () => clearTimeout(timer)
  }, [review?.check_status, refresh])
  async function checkAgain() {
    if (!data.scope || !review || busy) return
    setBusy(true)
    setError(null)
    try {
      const current = await apiFetch<CloseReview>('/api/subchain-closures/checks', {
        method: 'POST',
        body: JSON.stringify({ scope: data.scope, as_of_date: review.as_of_date }),
      })
      setSelected(current.id)
      setRefresh((value) => value + 1)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }
  async function close() {
    if (!review?.can_close || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await apiFetch<CloseReview>(
        `/api/subchain-closures/${encodeURIComponent(review.id)}/close`,
        {
          method: 'POST',
          body: JSON.stringify({
            check_run_id: review.check_run_id,
            input_fingerprint: review.input_fingerprint,
          }),
        }
      )
      if (result.status === 'closed') onResolved()
      else setRefresh((value) => value + 1)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }
  if (!selected) return <p>The dated review is unavailable. Refresh this task.</p>
  if (read.loading) return <Skeleton kind="cards" n={1} height={300} />
  if (read.error)
    return (
      <ErrorState
        what="dated subchain review"
        error={read.error}
        onRetry={() => setRefresh((value) => value + 1)}
      />
    )
  if (!review) return null
  const catalogueReady = hasRecordCatalogue(catalogue.value)
  const catalogueFailure = Boolean(catalogue.error)
  const closeAllowed = review.can_close && catalogueReady
  const tradeViews = actualTradeObjects(review)
  const unknownDateTradeIds = new Set(
    tradeViews.filter((trade) => trade.unknownDate).map((trade) => trade.object.id)
  )
  const objects = [
    ...review.objects.filter((object) => object.kind !== 'trade'),
    ...tradeViews.map((trade) => trade.object),
  ]
  const failed = review.checks.filter(
    (check) => !['passed', 'waived', 'not_applicable'].includes(check.result)
  )
  const earlier = review.prior_periods.filter((period) => period.status !== 'closed')
  const supplyIssues = review.objects.flatMap((object) =>
    object.kind === 'instrument'
      ? (object.dated_supply_intervals ?? [])
          .filter(
            (interval) => interval.status !== 'balanced' && !interval.waived_provisional?.length
          )
          .map((interval) => ({ object, interval }))
      : []
  )
  const shownSupplyBlockers = new Set(
    supplyIssues.map(
      ({ object, interval }) =>
        `instrument_supply_interval_${interval.status}:${object.id}:${interval.reference_id}`
    )
  )
  const otherBlockers = review.blockers.filter((value) => !shownSupplyBlockers.has(value))

  return (
    <article className="cnv ed-pane subchain-close" data-testid="subchain-close-card">
      <div className="cn-card">
        <div className="cn-ctit ed-card-band">
          <span className="k verify">Evidence</span>
        </div>
        <p className="ed-factual-sentence">
          Review this subchain as of {formatOfficerDate(review.as_of_date)}.
        </p>
        {catalogueFailure ? (
          <p className="subchain-close__attention" role="alert">
            Record field definitions are unavailable. Close is disabled until the Record catalogue
            loads.
          </p>
        ) : !catalogueReady ? (
          <p className="subchain-close__attention" role="status">
            Loading Record field definitions before this dated decision can be closed…
          </p>
        ) : null}
        {selected !== data.closure_id ? (
          <button
            type="button"
            className="cn-btn"
            onClick={() => setSelected(data.closure_id ?? null)}
          >
            Return to the latest period
          </button>
        ) : null}
        {earlier.length ? (
          <section className="subchain-close__attention">
            <p>An earlier period needs to be resolved first.</p>
            {earlier.map((period) => (
              <button
                className="cn-btn"
                type="button"
                key={period.id}
                onClick={() => setSelected(period.id)}
              >
                {formatOfficerDate(period.as_of_date)}
              </button>
            ))}
          </section>
        ) : null}
        {review.historical_only ? (
          <p className="subchain-close__attention">
            Historical review. This entry is no longer available in the current Record.
          </p>
        ) : null}
        {review.snapshot_current === false && !review.historical_only ? (
          <p className="subchain-close__attention">
            The record or documents changed. Run a fresh check before closing.
          </p>
        ) : null}
        {review.check_status === 'running' ? (
          <p role="status">Checking the subchain and its documents…</p>
        ) : null}
        {objects.map((object) => (
          <CurrentObject
            key={`${object.kind}:${object.id}`}
            object={object}
            catalogue={catalogue.value?.[object.kind]?.fields ?? []}
            balance={review.balances.find((balance) => balance.position_id === object.id)}
            unknownDate={unknownDateTradeIds.has(object.id)}
          />
        ))}
        {review.balances.map((balance) => (
          <section key={balance.position_id} className="subchain-close__balance">
            <h3>Balance and movements</h3>
            <table>
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Document</th>
                </tr>
              </thead>
              <tbody>
                {balance.base ? (
                  <tr>
                    <td>
                      {balance.base.kind === 'previous_close'
                        ? 'Last verified balance'
                        : 'Confirmed balance'}
                    </td>
                    <td>{formatOfficerDate(balance.base.date)}</td>
                    <td>{formatNumberString(balance.base.value)}</td>
                    <td>
                      {balance.base.document_id ? (
                        <DocumentLink document={{ document_id: balance.base.document_id }} />
                      ) : null}
                    </td>
                  </tr>
                ) : null}
                {balance.movements.map((movement) => (
                  <tr key={movement.trade_id}>
                    <td>Movement</td>
                    <td>{formatOfficerDate(movement.date)}</td>
                    <td>
                      {movement.delta === null ? 'Unknown' : formatNumberString(movement.delta)}
                    </td>
                    <td>
                      {movement.document_id ? (
                        <DocumentLink document={{ document_id: movement.document_id }} />
                      ) : null}
                    </td>
                  </tr>
                ))}
                <tr>
                  <th>Resulting balance</th>
                  <td>{formatOfficerDate(balance.value_date)}</td>
                  <td>
                    {balance.value === null ? 'Unknown' : formatNumberString(balance.value)}{' '}
                    {balance.currency}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
            {balance.comparisons
              .filter(
                (comparison) => !/^[-+]?0+(?:\.0+)?(?:[eE][-+]?\d+)?$/.test(comparison.residual)
              )
              .map((comparison) => (
                <p className="subchain-close__attention" key={comparison.date}>
                  Balance dated {formatOfficerDate(comparison.date)}:{' '}
                  {formatNumberString(comparison.stated)} stated;{' '}
                  {formatNumberString(comparison.expected_from_actual_trades)} expected from actual
                  trades since the previous verified balance.
                </p>
              ))}
            {balance.comparisons
              .filter((comparison) => (comparison.waived_provisional ?? []).length > 0)
              .map((comparison) => (
                <p className="subchain-close__attention" key={`${comparison.date}:waived`}>
                  A recorded provisional explanation covers this dated difference; it remains
                  separate from actual movements.
                </p>
              ))}
          </section>
        ))}
        {supplyIssues.map(({ object, interval }) => {
          const href = findingRecordHref(review, data.scope, {
            target_type: 'instrument',
            target_id: object.id,
          })
          return (
            <section
              className="subchain-close__attention"
              key={`${object.id}:${interval.reference_id}`}
            >
              <h3>{objectTitle(object)} · Issued balance</h3>
              <p>
                {formatOfficerDate(interval.previous_reference?.financial_date)} to{' '}
                {formatOfficerDate(interval.reference.financial_date)}:{' '}
                {formatIntervalQuantity(interval.stated)} stated;{' '}
                {interval.status === 'unknown' || interval.expected === null
                  ? 'the previous balance and recorded movements cannot establish the expected amount.'
                  : `${formatIntervalQuantity(interval.expected)} expected from the previous balance and recorded movements.`}
              </p>
              {interval.status === 'break' && interval.delta !== null ? (
                <p>Unexplained difference: {formatIntervalQuantity(interval.delta)}.</p>
              ) : null}
              {href ? (
                <Link className="subchain-close__record-link" to={href}>
                  Review instrument and movements in Record
                </Link>
              ) : null}
            </section>
          )
        })}
        {failed.length ? (
          <section className="subchain-close__attention">
            <h3>Evidence still needs attention</h3>
            {failed.map((check) => (
              <p key={`${check.target_type}:${check.target_id}:${check.requirement_code}`}>
                {check.detail || 'This requirement needs current evidence or a recorded waiver.'}
              </p>
            ))}
          </section>
        ) : null}
        {review.joint_review?.result?.findings.map((finding) => {
          const href = findingRecordHref(review, data.scope, finding)
          return (
            <section
              className="subchain-close__attention"
              key={`${finding.code}:${finding.target_type}:${finding.target_id}`}
            >
              <p>{finding.detail}</p>
              {finding.document_id ? (
                <DocumentLink
                  document={{ document_id: finding.document_id }}
                  context={{ page: finding.page }}
                />
              ) : null}
              {href ? (
                <Link className="subchain-close__record-link" to={href}>
                  {findingDoorLabel(review, finding)}
                </Link>
              ) : null}
            </section>
          )
        })}
        <details className="subchain-close__checks">
          <summary>Evidence checks</summary>
          {review.checks.map((check) => (
            <p key={`${check.target_type}:${check.target_id}:${check.requirement_code}`}>
              {['passed', 'waived', 'not_applicable'].includes(check.result) ? '✓' : '○'}{' '}
              {criteriaLabel(check)}
            </p>
          ))}
        </details>
        {otherBlockers.length && !failed.length && !earlier.length ? (
          <p className="subchain-close__attention">
            {review.blockers.some((value) => value.startsWith('booking_')) ? (
              <>
                Booking work needs review before this subchain can close.{' '}
                <Link
                  to={
                    '/v2/workbench?' +
                    new URLSearchParams(
                      review.scope?.fund_id
                        ? { scope: 'fund', fund: review.scope.fund_id, queue: 'book' }
                        : review.scope?.client_id
                          ? { scope: 'client', client: review.scope.client_id, queue: 'book' }
                          : { scope: 'all', queue: 'book' }
                    ).toString()
                  }
                >
                  Open Book
                </Link>
              </>
            ) : (
              'The dated record check is incomplete. Review the balances and source documents before checking again.'
            )}
          </p>
        ) : null}
        {review.joint_review?.error ? (
          <p className="subchain-close__attention">
            The joint document check could not complete. Request a fresh check.
          </p>
        ) : null}
        {error ? <ErrorState what="subchain decision" error={error} /> : null}
        <div className="cn-abar">
          {review.status === 'closed' ? (
            <span>Closed</span>
          ) : (
            <button
              type="button"
              className="cn-btn p verify"
              disabled={busy || !closeAllowed}
              onClick={() => void close()}
            >
              Close subchain
            </button>
          )}
          {!review.historical_only && !review.can_close && review.check_status !== 'running' ? (
            <button
              type="button"
              className="cn-btn"
              disabled={busy || !data.scope}
              onClick={() => void checkAgain()}
            >
              Check again
            </button>
          ) : null}
        </div>
        <SourceDocumentList documents={review.documents} />
      </div>
    </article>
  )
}
